/**
 * QR Code Dialog Component
 *
 * Displays a QR code in a larger dialog overlay with site name and bay info.
 * Uses ShieldSans font for all content per typography guidelines.
 */

import { useEffect, useCallback } from 'react';
import { embedPngMetadata, buildQrMetadata } from '../../qr-customize/utils/pngMetadata';
import './QrCodeDialog.css';

interface QrCodeDialogProps {
  readonly imageUrl: string;
  readonly siteName: string;
  readonly bayId?: string;
  readonly bayName?: string;
  readonly downloadFilename?: string;
  /** The URL encoded in the QR code, for metadata embedding */
  readonly encodedUrl?: string;
  /** The author (user name/email) for metadata embedding */
  readonly author?: string;
  readonly onClose: () => void;
  /** Optional custom download handler. If provided, overrides the default download behavior. */
  readonly onDownload?: () => void;
  /** Whether the image is full-size (1024px). When true, dialog scales to fit screen. */
  readonly isFullSize?: boolean;
}

/**
 * QrCodeDialog component for displaying a QR code in a larger view.
 *
 * @param imageUrl - The URL of the QR code image
 * @param siteName - The name of the site
 * @param bayId - Optional bay ID for bay QR codes
 * @param bayName - Optional bay name for super bays
 * @param downloadFilename - Optional filename for download
 * @param onClose - Callback to close the dialog
 */
export function QrCodeDialog({
  imageUrl,
  siteName,
  bayId,
  bayName,
  downloadFilename = 'qr-code.png',
  encodedUrl,
  author,
  onClose,
  onDownload,
  isFullSize = false,
}: QrCodeDialogProps): JSX.Element {
  // Close on Escape key
  const handleKeyDown = useCallback(
    (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    },
    [onClose]
  );

  useEffect(() => {
    document.addEventListener('keydown', handleKeyDown);
    // Prevent body scroll when dialog is open
    document.body.style.overflow = 'hidden';

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.body.style.overflow = '';
    };
  }, [handleKeyDown]);

  // Build subtitle based on whether it's a site or bay QR
  const buildSubtitle = (): string => {
    if (!bayId) {
      return siteName;
    }
    if (bayName && bayName !== '-') {
      return `${siteName} - Bay ${bayId} (${bayName})`;
    }
    return `${siteName} - Bay ${bayId}`;
  };

  const handleOverlayClick = (event: React.MouseEvent<HTMLDivElement>): void => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  const handleDownload = (): void => {
    // Use custom download handler if provided
    if (onDownload) {
      onDownload();
      return;
    }

    let downloadUrl = imageUrl;
    
    // Embed metadata if we have the encoded URL and author
    if (encodedUrl && author) {
      const metadata = buildQrMetadata(siteName, encodedUrl, author);
      downloadUrl = embedPngMetadata(imageUrl, metadata);
    }
    
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = downloadFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="qr-code-dialog__overlay"
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="qr-dialog-title"
    >
      <div className={`qr-code-dialog${isFullSize ? ' qr-code-dialog--full-size' : ''}`}>
        <button
          type="button"
          className="qr-code-dialog__close-button"
          onClick={onClose}
          aria-label="Close dialog"
        >
          ×
        </button>
        <div className="qr-code-dialog__content">
          <img
            src={imageUrl}
            alt="QR Code"
            className={`qr-code-dialog__image${isFullSize ? ' qr-code-dialog__image--full-size' : ''}`}
          />
          <p id="qr-dialog-title" className="qr-code-dialog__subtitle">
            {buildSubtitle()}
          </p>
          <button
            type="button"
            className="qr-code-dialog__download-button"
            onClick={handleDownload}
          >
            Download
          </button>
        </div>
      </div>
    </div>
  );
}
